import { ComponentType } from 'react';

interface IconProps {
  className?: string;
  color?: string;
}

export interface TargetAudience {
  icon: ComponentType<IconProps>;
  title: string;
  description: string;
  badge: string;
}

interface ServiceTargetSectionProps {
  title?: string;
  subtitle?: string;
  audiences: TargetAudience[];
  accentColor?: string;
}

export function ServiceTargetSection({
  title = "Pour qui ?",
  subtitle,
  audiences,
  accentColor = "blue",
}: ServiceTargetSectionProps) {
  const colorClasses = {
    blue: {
      iconBg: "bg-blue-50",
      iconText: "text-blue-600",
      badgeBg: "bg-blue-100",
      badgeText: "text-blue-700",
    },
    green: {
      iconBg: "bg-green-50",
      iconText: "text-green-600",
      badgeBg: "bg-green-100",
      badgeText: "text-green-700",
    },
    orange: {
      iconBg: "bg-orange-50",
      iconText: "text-orange-600",
      badgeBg: "bg-orange-100",
      badgeText: "text-orange-700",
    },
    red: {
      iconBg: "bg-red-50",
      iconText: "text-red-600",
      badgeBg: "bg-red-100",
      badgeText: "text-red-700",
    },
    purple: {
      iconBg: "bg-purple-50",
      iconText: "text-purple-600",
      badgeBg: "bg-purple-100",
      badgeText: "text-purple-700",
    },
  };

  const colors = colorClasses[accentColor as keyof typeof colorClasses] || colorClasses.blue;

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-3">
              {title}
            </h2>
            {subtitle && (
              <p className="text-aspexit-gray text-lg max-w-2xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {audiences.map((audience, index) => {
              const Icon = audience.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl border-2 border-border p-8 hover:shadow-lg transition-all hover:-translate-y-1"
                >
                  {/* Icon */}
                  <div className={`w-16 h-16 ${colors.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                    <Icon className="w-8 h-8" color={accentColor === 'blue' ? '#2563EB' : accentColor === 'green' ? '#55BC8C' : accentColor === 'orange' ? '#FB923C' : accentColor === 'red' ? '#EF4444' : '#A855F7'} />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-heading font-semibold text-aspexit-black mb-3">
                    {audience.title}
                  </h3>

                  {/* Description */}
                  <p className="text-aspexit-gray text-sm leading-relaxed">
                    {audience.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
